import { useMutation, useQueryClient } from 'react-query';
import notify from '../../../../../../../shared/feedback/notifications';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useTelemetryContext } from '../../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../../telemetry/utils';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { photoStatusQueryKey } from '../../entry/queries/use-query-photo-status';
import { imagesQueryKey } from '../../listImages/queries/use-query-images';
import { UploadVariant } from '../state/utils';

type UploadImagesMutationArgs = {
    orderId: number;
    orderRowId: number;
    variant: UploadVariant;
    user: AuthenticatedUser;
    onFileUploaded: (uploadedCount: number) => void;
    onUploaded?: (uploadedFilenames: string[]) => void;
};

export type UploadImagesResult = {
    uploadedFiles: File[];
    failedFiles: File[];
};

async function uploadOne(
    orderId: number,
    orderRowId: number,
    variant: UploadVariant,
    file: File
): Promise<void> {
    if (variant === UploadVariant.Normal) {
        let response = await ImageProcessingApi.uploadNormalImage(orderId, orderRowId, file);
        await ImageProcessingApi.setNormalImageInProgress(orderId, orderRowId, response);
        return;
    }

    let response = await ImageProcessingApi.uploadEditedImage(orderId, orderRowId, file);
    await ImageProcessingApi.setImageInProgress(orderId, orderRowId, response);
}

export function useUploadImagesMutation({
    orderId,
    orderRowId,
    variant,
    user,
    onFileUploaded,
    onUploaded
}: UploadImagesMutationArgs) {
    let queryClient = useQueryClient();
    let { logger } = useTelemetryContext();

    return useMutation(
        async (files: File[]): Promise<UploadImagesResult> => {
            let uploadedFiles: File[] = [];
            let failedFiles: File[] = [];

            for (let file of files) {
                try {
                    await uploadOne(orderId, orderRowId, variant, file);
                    uploadedFiles.push(file);
                } catch (error) {
                    failedFiles.push(file);
                    logger.logException(error, {
                        origin: 'ImageEditorImageProcessingPage.Mutation.uploadImage',
                        userInfo: getUserInfo(user),
                        filename: file.name,
                        variant
                    });
                }
                onFileUploaded(uploadedFiles.length + failedFiles.length);
            }

            return { uploadedFiles, failedFiles };
        },
        {
            onSuccess: async ({ uploadedFiles, failedFiles }) => {
                failedFiles.forEach((file) =>
                    notify.failure(
                        Translate('image-processing/edited/file-upload/failed/text', {
                            filename: file.name
                        })
                    )
                );

                if (uploadedFiles.length === 0) {
                    return;
                }

                if (onUploaded) {
                    onUploaded(uploadedFiles.map((file) => file.name));
                }

                if (variant === UploadVariant.Normal) {
                    notify.success(Translate('image-processing/edited/normal-upload/message/done'));
                } else {
                    notify.success(Translate('image-processing/notification/images-uploaded'));
                }

                await Promise.all([
                    queryClient.cancelQueries([imagesQueryKey, { orderId }]),
                    queryClient.cancelQueries([photoStatusQueryKey, { orderId }])
                ]);

                return Promise.all([
                    queryClient.invalidateQueries([imagesQueryKey, { orderId }]),
                    queryClient.invalidateQueries([photoStatusQueryKey, { orderId }])
                ]);
            }
        }
    );
}
